import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Landing from "./pages/Landing";
import Precios from "./pages/public/Precios";
import Servicios from "./pages/public/Servicios";
import LegalPage from "./pages/public/LegalPage";
import VerificarBoleta from "./pages/public/VerificarBoleta";
import Agendar, { ReservaAgenda } from "./pages/public/Agendar";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import PlanCuentas from "./pages/PlanCuentas";
import CentrosCosto from "./pages/CentrosCosto";
import LibroDiario from "./pages/LibroDiario";
import LibroMayor from "./pages/LibroMayor";
import BalanceGeneral from "./pages/BalanceGeneral";
import Balance8Columnas from "./pages/Balance8Columnas";
import EstadoResultados from "./pages/EstadoResultados";
import { Clientes, Proveedores } from "./pages/Contactos";
import { LibroVentas, LibroCompras } from "./pages/LibrosIVA";
import BoletasHonorarios from "./pages/BoletasHonorarios";
import Facturador from "./pages/Facturador";
import DocumentosRecibidos from "./pages/DocumentosRecibidos";
import FlujoCaja from "./pages/FlujoCaja";
import CuentasBancarias from "./pages/CuentasBancarias";
import Cartola from "./pages/Cartola";
import Conciliacion from "./pages/Conciliacion";
import PagosProveedores from "./pages/PagosProveedores";
import CuentaCorriente from "./pages/CuentaCorriente";
import CajaChica from "./pages/CajaChica";
import ActivoFijo from "./pages/ActivoFijo";
import Existencias from "./pages/Existencias";
import Provisiones from "./pages/Provisiones";
import Declaraciones from "./pages/Declaraciones";
import Multimoneda from "./pages/Multimoneda";
import Empresas from "./pages/Empresas";
import Usuarios from "./pages/Usuarios";
import Remuneraciones from "./pages/remuneraciones/Remuneraciones";
import PortalColaborador from "./pages/remuneraciones/PortalColaborador";
import SubscriptionBlocked from "./components/SubscriptionBlocked";
import Organizador from "./pages/Organizador";
import InformeTributario from "./pages/informeTributario/InformeTributario";
import AgendaAdmin from "./pages/AgendaAdmin";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground bg-background">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const blocked =
    user.organizacion_expirada ||
    (user.organizacion_id && user.organizacion_activa === false);
  if (blocked && user.role !== "superadmin") {
    return <SubscriptionBlocked />;
  }
  return children;
}

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground bg-background">
      Cargando…
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <AuthLoading />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="aequor-theme">
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/precios" element={<Precios />} />
          <Route path="/servicios" element={<Servicios />} />
          <Route path="/terminos" element={<LegalPage doc="terminos" />} />
          <Route path="/privacidad" element={<LegalPage doc="privacidad" />} />
          <Route path="/reembolsos" element={<LegalPage doc="reembolsos" />} />
          <Route path="/verificar" element={<VerificarBoleta />} />
          <Route path="/agendar" element={<Agendar />} />
          <Route path="/agendar/reserva/:id" element={<ReservaAgenda />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/olvide-contraseña" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/restablecer-contraseña" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          <Route path="/portal-colaborador" element={<PortalColaborador />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="organizador" element={<Organizador />} />
            <Route path="plan-cuentas" element={<PlanCuentas />} />
            <Route path="centros-costo" element={<CentrosCosto />} />
            <Route path="libro-diario" element={<LibroDiario />} />
            <Route path="activo-fijo" element={<ActivoFijo />} />
            <Route path="existencias" element={<Existencias />} />
            <Route path="provisiones" element={<Provisiones />} />
            <Route path="multimoneda" element={<Multimoneda />} />
            <Route path="libro-mayor" element={<LibroMayor />} />
            <Route path="balance-general" element={<BalanceGeneral />} />
            <Route path="balance-8-columnas" element={<Balance8Columnas />} />
            <Route path="estado-resultados" element={<EstadoResultados />} />
            <Route path="clientes" element={<Clientes />} />
            <Route path="proveedores" element={<Proveedores />} />
            <Route path="libro-ventas" element={<LibroVentas />} />
            <Route path="libro-compras" element={<LibroCompras />} />
            <Route path="boletas-honorarios" element={<BoletasHonorarios />} />
            <Route path="facturador" element={<Facturador />} />
            <Route path="documentos-recibidos" element={<DocumentosRecibidos />} />
            <Route path="flujo-caja" element={<FlujoCaja />} />
            <Route path="informe-tributario" element={<InformeTributario />} />
            <Route path="declaraciones" element={<Declaraciones />} />
            <Route path="cuentas-bancarias" element={<CuentasBancarias />} />
            <Route path="cartola" element={<Cartola />} />
            <Route path="conciliacion" element={<Conciliacion />} />
            <Route path="cuenta-corriente" element={<CuentaCorriente />} />
            <Route path="pagos-proveedores" element={<PagosProveedores />} />
            <Route path="caja-chica" element={<CajaChica />} />
            <Route path="remuneraciones/*" element={<Remuneraciones />} />
            <Route path="empresas" element={<Empresas />} />
            <Route path="usuarios" element={<Usuarios />} />
            <Route path="agenda" element={<AgendaAdmin />} />
          </Route>
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
